import { type ReactNode, useRef } from 'react';
import { haptic } from '../lib/haptic';
import { playSfx } from '../lib/sfx';
import './Tile.css';

type Tone =
  | 'pink' | 'rose' | 'coral' | 'orange' | 'yellow' | 'lime'
  | 'green' | 'aqua' | 'teal' | 'blue' | 'indigo' | 'purple' | 'violet'
  | 'dark';

type Props = {
  title: string;
  tone: Tone;
  icon: ReactNode;
  onClick: () => void;
  tileId?: string;
};

export function Tile({ title, tone, icon, onClick, tileId }: Props) {
  const iconRef = useRef<HTMLSpanElement>(null);

  const handleClick = () => {
    haptic('light');
    playSfx('tap');
    // Tag this tile's icon so the browser's View Transition morphs it into
    // the incoming view (see ViewTransition.tsx) — set synchronously so
    // it's present in the "old" state capture before onClick triggers the
    // transition. The tile unmounts as part of that same transition, so
    // there's nothing to clean up on this element afterward.
    if (iconRef.current) {
      iconRef.current.style.viewTransitionName = 'tile-morph';
    }
    onClick();
  };

  return (
    <button type="button" className={`tile tile--${tone}`} onClick={handleClick}>
      <span className="tile-icon" aria-hidden="true" ref={iconRef} data-tile-id={tileId}>
        <span className="tile-icon-glyph">{icon}</span>
      </span>
      <span className="tile-title">{title}</span>
    </button>
  );
}

export type { Tone };
